import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import AfficheCard from '../components/common/AfficheCard';

const s = {
  label:   { display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' },
  input:   { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '0.92rem', color: '#1e293b', boxSizing: 'border-box', background: 'white' },
  section: { backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)', boxShadow: 'var(--card-shadow)', marginBottom: '16px', overflow: 'hidden' },
  sHead:   { padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'var(--page-bg)' },
  sBody:   { padding: '18px 20px' },
  sTitle:  { fontWeight: 700, fontSize: '0.97rem', color: 'var(--text-primary)' },
  sDesc:   { fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '1px' },
  twoCol:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  subHead: { fontWeight: 700, fontSize: '0.85rem', color: '#374151', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 },
  dot:     { width: 6, height: 6, borderRadius: '50%', background: '#7c3aed', display: 'inline-block', flexShrink: 0 },
};

const ICON_OPTIONS = [
  { value: 'users',  label: 'Membres (users)' },
  { value: 'check',  label: 'Présences (check)' },
  { value: 'bar',    label: 'Statistiques (bar)' },
  { value: 'qr',     label: 'QR Code (qr)' },
  { value: 'cal',    label: 'Calendrier (cal)' },
  { value: 'bell',   label: 'Notifications (bell)' },
  { value: 'church', label: 'Église (church)' },
];

const DEFAULT_FEATURES = [
  { icon: 'users', title: 'Gestion des membres', desc: 'Enregistrez et suivez chaque fidèle : coordonnées, statut, présences et carte membre numérique.' },
  { icon: 'check', title: 'Suivi des présences', desc: 'Pointage rapide par scan QR ou saisie manuelle, avec historique complet par activité.' },
  { icon: 'bar',   title: 'Statistiques avancées', desc: 'Visualisez la croissance, la fréquentation et les tendances de votre communauté en temps réel.' },
  { icon: 'qr',   title: 'Scan QR intelligent', desc: "Chaque fidèle dispose d'un QR personnel. Les agents scannent à l'entrée en quelques secondes." },
];

export default function LandingSettings() {
  const [saving, setSaving] = useState(false);

  // Favicon
  const [faviconUrl, setFaviconUrl] = useState('');
  const [faviconFile, setFaviconFile] = useState(null);
  const [removeFavicon, setRemoveFavicon] = useState(false);
  const faviconInputRef = useRef(null);

  // Logo écran de chargement (splash PWA, config globale)
  const [splashLogoUrl, setSplashLogoUrl] = useState('');
  const [splashLogoFile, setSplashLogoFile] = useState(null);
  const [removeSplashLogo, setRemoveSplashLogo] = useState(false);
  const splashLogoInputRef = useRef(null);
  const [splashBgColor, setSplashBgColor] = useState('');
  const [splashBgTransparent, setSplashBgTransparent] = useState(true);

  // Emails de notification pour les demandes de contact (config globale)
  const [contactEmails, setContactEmails] = useState('');

  // Marque (nom + logo affichés dans la navbar/titre/footer de la page d'accueil)
  const [brandName, setBrandName] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [removeBrandLogo, setRemoveBrandLogo] = useState(false);
  const brandLogoInputRef = useRef(null);

  // Hero
  const [heroBadge, setHeroBadge] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [phoneMemberName, setPhoneMemberName] = useState('');
  const [phoneMemberRole, setPhoneMemberRole] = useState('');
  const [ctaPrimary, setCtaPrimary] = useState('Se connecter');
  const [ctaSecondary, setCtaSecondary] = useState('Espace fidèle');

  // Arrière-plan du hero
  const [bgFrom, setBgFrom] = useState('#e0e7ff');
  const [bgTo, setBgTo] = useState('#dbeafe');
  const [bgImageUrl, setBgImageUrl] = useState('');
  const [bgImageFile, setBgImageFile] = useState(null);
  const [removeBgImage, setRemoveBgImage] = useState(false);
  const bgImageInputRef = useRef(null);
  const [overlayColor, setOverlayColor] = useState('#ffffff');
  const [overlayOpacity, setOverlayOpacity] = useState(0.6);
  const [imageOpacity, setImageOpacity] = useState(0.16);
  const [titleColor, setTitleColor] = useState('');
  const [subtitleColor, setSubtitleColor] = useState('');

  // Verset
  const [verse, setVerse] = useState('');
  const [verseRef, setVerseRef] = useState('');

  // Activités
  const [showActivities, setShowActivities] = useState(true);
  const [activitiesTitle, setActivitiesTitle] = useState('Activités à venir');

  // Fonctionnalités
  const [features, setFeatures] = useState(DEFAULT_FEATURES);

  // Actualités
  const [news, setNews] = useState([]);

  // Témoignages
  const [testimonials, setTestimonials] = useState([]);

  // Qui sommes-nous / Vision
  const [aboutTitle, setAboutTitle] = useState('');
  const [aboutText, setAboutText] = useState('');
  const [aboutImageUrl, setAboutImageUrl] = useState('');
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [removeAboutImage, setRemoveAboutImage] = useState(false);
  const [visionTitle, setVisionTitle] = useState('');
  const [visionText, setVisionText] = useState('');

  // Départements / Programmes
  const [departments, setDepartments] = useState([]);
  const [programs, setPrograms] = useState([]);

  // Diffusion en direct
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [liveUrl, setLiveUrl] = useState('');
  const [liveTitle, setLiveTitle] = useState('Diffusion en direct');

  // Charger depuis l'API au montage
  useEffect(() => {
    api.get('/notifications/church-settings/')
      .then(r => {
        setFaviconUrl(r.data.favicon || '');
        setSplashLogoUrl(r.data.platform_splash_logo || '');
        setSplashBgColor(r.data.platform_splash_bg_color || '');
        setSplashBgTransparent(!r.data.platform_splash_bg_color);
        setContactEmails(r.data.contact_notification_emails || '');
        setBrandName(r.data.landing_brand_name || '');
        setBrandLogoUrl(r.data.landing_brand_logo || '');
        setHeroBadge(r.data.landing_hero_badge || '');
        setTitle(r.data.landing_title || '');
        setSubtitle(r.data.landing_subtitle || '');
        setCtaPrimary(r.data.landing_cta_primary || 'Se connecter');
        setCtaSecondary(r.data.landing_cta_secondary || 'Espace fidèle');
        setVerse(r.data.landing_verse || '');
        setVerseRef(r.data.landing_verse_ref || '');
        setBgFrom(r.data.landing_bg_from || '#e0e7ff');
        setBgTo(r.data.landing_bg_to || '#dbeafe');
        setBgImageUrl(r.data.landing_bg_image || '');
        setOverlayColor(r.data.landing_overlay_color || '#ffffff');
        setOverlayOpacity(r.data.landing_overlay_opacity ?? 0.6);
        setImageOpacity(r.data.landing_image_opacity ?? 0.16);
        setTitleColor(r.data.landing_title_color || '');
        setSubtitleColor(r.data.landing_subtitle_color || '');
        setShowActivities(r.data.landing_show_activities !== false);
        setActivitiesTitle(r.data.landing_activities_title || 'Activités à venir');
        setPhoneMemberName(r.data.landing_phone_member_name || '');
        setPhoneMemberRole(r.data.landing_phone_member_role || '');
        if (r.data.landing_features && r.data.landing_features.length > 0) {
          setFeatures(r.data.landing_features);
        }
        if (r.data.landing_news) setNews(r.data.landing_news);
        if (r.data.landing_testimonials) setTestimonials(r.data.landing_testimonials);
        setAboutTitle(r.data.landing_about_title || '');
        setAboutText(r.data.landing_about_text || '');
        setAboutImageUrl(r.data.landing_about_image || '');
        setVisionTitle(r.data.landing_vision_title || '');
        setVisionText(r.data.landing_vision_text || '');
        if (r.data.landing_departments) setDepartments(r.data.landing_departments);
        if (r.data.landing_programs) setPrograms(r.data.landing_programs);
        setLiveEnabled(!!r.data.landing_live_enabled);
        setLiveUrl(r.data.landing_live_url || '');
        setLiveTitle(r.data.landing_live_title || 'Diffusion en direct');
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('landing_brand_name', brandName);
      fd.append('landing_hero_badge', heroBadge);
      fd.append('landing_title', title);
      fd.append('landing_subtitle', subtitle);
      fd.append('landing_cta_primary', ctaPrimary);
      fd.append('landing_cta_secondary', ctaSecondary);
      fd.append('landing_verse', verse);
      fd.append('landing_verse_ref', verseRef);
      fd.append('landing_bg_from', bgFrom);
      fd.append('landing_bg_to', bgTo);
      fd.append('landing_overlay_color', overlayColor);
      fd.append('landing_overlay_opacity', String(overlayOpacity));
      fd.append('landing_image_opacity', String(imageOpacity));
      fd.append('landing_title_color', titleColor);
      fd.append('landing_subtitle_color', subtitleColor);
      fd.append('platform_splash_bg_color', splashBgTransparent ? '' : splashBgColor);
      fd.append('contact_notification_emails', contactEmails);
      if (bgImageFile) {
        fd.append('landing_bg_image', bgImageFile);
      } else if (removeBgImage) {
        fd.append('remove_landing_bg_image', 'true');
      }
      fd.append('landing_show_activities', showActivities ? 'true' : 'false');
      fd.append('landing_activities_title', activitiesTitle);
      fd.append('landing_phone_member_name', phoneMemberName);
      fd.append('landing_phone_member_role', phoneMemberRole);
      fd.append('landing_features', JSON.stringify(features));
      fd.append('landing_news', JSON.stringify(news));
      fd.append('landing_testimonials', JSON.stringify(testimonials));
      fd.append('landing_about_title', aboutTitle);
      fd.append('landing_about_text', aboutText);
      fd.append('landing_vision_title', visionTitle);
      fd.append('landing_vision_text', visionText);
      fd.append('landing_departments', JSON.stringify(departments));
      fd.append('landing_programs', JSON.stringify(programs));
      fd.append('landing_live_enabled', liveEnabled ? 'true' : 'false');
      fd.append('landing_live_url', liveUrl);
      fd.append('landing_live_title', liveTitle);
      if (aboutImageFile) {
        fd.append('landing_about_image', aboutImageFile);
      } else if (removeAboutImage) {
        fd.append('remove_landing_about_image', 'true');
      }
      if (faviconFile) {
        fd.append('favicon', faviconFile);
      } else if (removeFavicon) {
        fd.append('remove_favicon', 'true');
      }
      if (splashLogoFile) {
        fd.append('platform_splash_logo', splashLogoFile);
      } else if (removeSplashLogo) {
        fd.append('remove_platform_splash_logo', 'true');
      }
      if (brandLogoFile) {
        fd.append('landing_brand_logo', brandLogoFile);
      } else if (removeBrandLogo) {
        fd.append('remove_landing_brand_logo', 'true');
      }
      // Sans church_id → config globale
      const res = await api.put('/notifications/church-settings/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFaviconUrl(res.data.favicon || '');
      setFaviconFile(null);
      setRemoveFavicon(false);
      setSplashLogoUrl(res.data.platform_splash_logo || '');
      setSplashLogoFile(null);
      setRemoveSplashLogo(false);
      setSplashBgColor(res.data.platform_splash_bg_color || '');
      setSplashBgTransparent(!res.data.platform_splash_bg_color);
      setContactEmails(res.data.contact_notification_emails || '');
      setBrandLogoUrl(res.data.landing_brand_logo || '');
      setBrandLogoFile(null);
      setRemoveBrandLogo(false);
      setBgImageUrl(res.data.landing_bg_image || '');
      setBgImageFile(null);
      setRemoveBgImage(false);
      setAboutImageUrl(res.data.landing_about_image || '');
      setAboutImageFile(null);
      setRemoveAboutImage(false);
      toast.success('Page d\'accueil enregistrée !');
    } catch {
      toast.error('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (i, key, val) =>
    setFeatures(prev => prev.map((f, j) => j === i ? { ...f, [key]: val } : f));

  return (
    <div style={{ width: '100%' }}>

      {/* ── En-tête page ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Page d'accueil</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '4px', marginBottom: 0 }}>
            Personnalisez la page publique visible par tous vos visiteurs.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a href="/" target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#7c3aed', fontWeight: 600, textDecoration: 'none', padding: '9px 14px', border: '1px solid #e9d5ff', borderRadius: 8, background: '#faf5ff' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Voir la page
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '9px 22px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.92rem', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0f4ff', borderRadius: 10, border: '1px solid #c7d2fe', fontSize: '0.8rem', color: '#4338ca', display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        Ces paramètres s'appliquent à la page publique partagée par toutes les églises (config globale).
      </div>

      {/* ═══ SECTION FAVICON ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>
          <div>
            <div style={s.sTitle}>Favicon</div>
            <div style={s.sDesc}>Icône affichée dans l'onglet du navigateur (format carré recommandé, PNG ou ICO)</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {faviconFile ? (
                <img src={URL.createObjectURL(faviconFile)} alt="favicon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (faviconUrl && !removeFavicon) ? (
                <img src={faviconUrl} alt="favicon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
              )}
            </div>
            <input ref={faviconInputRef} type="file" accept="image/*,.ico" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) { setFaviconFile(file); setRemoveFavicon(false); }
              }} />
            <button onClick={() => faviconInputRef.current?.click()}
              style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              Choisir une image
            </button>
            {(faviconUrl || faviconFile) && !removeFavicon && (
              <button onClick={() => { setFaviconFile(null); setRemoveFavicon(true); if (faviconInputRef.current) faviconInputRef.current.value = ''; }}
                style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>
                Supprimer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══ SECTION LOGO ÉCRAN DE CHARGEMENT (SPLASH PWA) ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          <div>
            <div style={s.sTitle}>Logo de l'écran de chargement</div>
            <div style={s.sDesc}>Logo affiché au démarrage de l'application (splash PWA) — appliqué à toutes les églises</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 64, height: 64, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {splashLogoFile ? (
                <img src={URL.createObjectURL(splashLogoFile)} alt="logo splash" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (splashLogoUrl && !removeSplashLogo) ? (
                <img src={splashLogoUrl} alt="logo splash" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              )}
            </div>
            <input ref={splashLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) { setSplashLogoFile(file); setRemoveSplashLogo(false); }
              }} />
            <button onClick={() => splashLogoInputRef.current?.click()}
              style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              Choisir une image
            </button>
            {(splashLogoUrl || splashLogoFile) && !removeSplashLogo && (
              <button onClick={() => { setSplashLogoFile(null); setRemoveSplashLogo(true); if (splashLogoInputRef.current) splashLogoInputRef.current.value = ''; }}
                style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>
                Supprimer
              </button>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 10 }}>Format carré recommandé (PNG, fond transparent). Si vide, le logo par défaut est utilisé.</div>

          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <label style={s.label}>Couleur de fond de l'écran de chargement</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
              <input type="checkbox" checked={splashBgTransparent}
                onChange={e => setSplashBgTransparent(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#7c3aed' }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Fond transparent (aucune couleur)</span>
            </label>
            {!splashBgTransparent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={splashBgColor || '#ffffff'} onChange={e => setSplashBgColor(e.target.value)}
                  style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                <input style={{ ...s.input, flex: 1, maxWidth: 160 }} value={splashBgColor} onChange={e => setSplashBgColor(e.target.value)} placeholder="#ffffff" />
              </div>
            )}
          </div>

          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <label style={s.label}>Emails de notification — demandes de contact</label>
            <textarea rows={3} style={{ ...s.input, resize: 'vertical' }} value={contactEmails}
              onChange={e => setContactEmails(e.target.value)} placeholder={"contact@exemple.com\nadmin@exemple.com"} />
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 6 }}>Un email par ligne. Ces adresses recevront une notification à chaque nouvelle demande envoyée depuis la page d'accueil.</div>
          </div>
        </div>
      </div>

      {/* ═══ SECTION HERO ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          <div>
            <div style={s.sTitle}>Section Hero</div>
            <div style={s.sDesc}>Titre principal, sous-titre et boutons d'action</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>
              Nom de la marque
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>(affiché dans la barre de navigation, le titre et le pied de page)</span>
            </label>
            <input style={s.input} value={brandName} onChange={e => setBrandName(e.target.value)}
              placeholder="RCC" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>
              Logo de la marque
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>(affiché dans la barre de navigation et le pied de page)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {brandLogoFile ? (
                  <img src={URL.createObjectURL(brandLogoFile)} alt="logo marque" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (brandLogoUrl && !removeBrandLogo) ? (
                  <img src={brandLogoUrl} alt="logo marque" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                )}
              </div>
              <input ref={brandLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) { setBrandLogoFile(file); setRemoveBrandLogo(false); }
                }} />
              <button onClick={() => brandLogoInputRef.current?.click()}
                style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                Choisir une image
              </button>
              {(brandLogoUrl || brandLogoFile) && !removeBrandLogo && (
                <button onClick={() => { setBrandLogoFile(null); setRemoveBrandLogo(true); if (brandLogoInputRef.current) brandLogoInputRef.current.value = ''; }}
                  style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>
                  Supprimer
                </button>
              )}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 6 }}>Si vide, l'icône par défaut est utilisée.</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>
              Badge d'accroche
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>(ex: Nouveau · Application mobile disponible)</span>
            </label>
            <input style={s.input} value={heroBadge} onChange={e => setHeroBadge(e.target.value)}
              placeholder="Laisser vide pour masquer le badge" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Titre principal</label>
            <input style={s.input} value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Gestion des Présences" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Sous-titre</label>
            <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={subtitle}
              onChange={e => setSubtitle(e.target.value)}
              placeholder="La plateforme multi-églises pour gérer vos fidèles, présences et activités." />
          </div>
          <div style={s.twoCol}>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Texte bouton principal</label>
              <input style={s.input} value={ctaPrimary} onChange={e => setCtaPrimary(e.target.value)}
                placeholder="Se connecter" />
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>Redirige vers le dashboard ou la connexion</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Texte bouton secondaire</label>
              <input style={s.input} value={ctaSecondary} onChange={e => setCtaSecondary(e.target.value)}
                placeholder="Espace fidèle" />
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>Redirige vers le portail fidèle</div>
            </div>
          </div>
          <div style={s.twoCol}>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Nom affiché sur l'aperçu téléphone</label>
              <input style={s.input} value={phoneMemberName} onChange={e => setPhoneMemberName(e.target.value)}
                placeholder="Jonathan Touré" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Statut affiché sur l'aperçu téléphone</label>
              <input style={s.input} value={phoneMemberRole} onChange={e => setPhoneMemberRole(e.target.value)}
                placeholder="Membre actif" />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ SECTION ARRIÈRE-PLAN DU HERO ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
          <div>
            <div style={s.sTitle}>Arrière-plan du hero</div>
            <div style={s.sDesc}>Dégradé de couleurs et/ou image de fond pour la section d'accueil</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={s.twoCol}>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Couleur de départ du dégradé</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={bgFrom} onChange={e => setBgFrom(e.target.value)}
                  style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                <input style={{ ...s.input, flex: 1 }} value={bgFrom} onChange={e => setBgFrom(e.target.value)} placeholder="#e0e7ff" />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={s.label}>Couleur de fin du dégradé</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={bgTo} onChange={e => setBgTo(e.target.value)}
                  style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                <input style={{ ...s.input, flex: 1 }} value={bgTo} onChange={e => setBgTo(e.target.value)} placeholder="#dbeafe" />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 6, borderRadius: 10, height: 48, background: `linear-gradient(135deg, ${bgFrom} 0%, ${bgTo} 100%)`, border: '1px solid var(--border)' }} />
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 16 }}>Aperçu du dégradé</div>

          <div>
            <label style={s.label}>
              Image de fond <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel — superposée au dégradé)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
              <div style={{ width: 90, height: 50, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {bgImageFile ? (
                  <img src={URL.createObjectURL(bgImageFile)} alt="fond" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (bgImageUrl && !removeBgImage) ? (
                  <img src={bgImageUrl} alt="fond" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                )}
              </div>
              <input ref={bgImageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) { setBgImageFile(file); setRemoveBgImage(false); }
                }} />
              <button onClick={() => bgImageInputRef.current?.click()}
                style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                Choisir une image
              </button>
              {(bgImageUrl || bgImageFile) && !removeBgImage && (
                <button onClick={() => { setBgImageFile(null); setRemoveBgImage(true); if (bgImageInputRef.current) bgImageInputRef.current.value = ''; }}
                  style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>
                  Supprimer
                </button>
              )}
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <label style={s.label}>Intensité de l'image : {Math.round(imageOpacity * 100)}%</label>
            <input type="range" min="0" max="1" step="0.05" value={imageOpacity}
              onChange={e => setImageOpacity(parseFloat(e.target.value))}
              style={{ width: '100%', marginTop: 10 }} />
          </div>

          <div style={{ marginTop: 20 }}>
            <label style={s.label}>
              Voile sur l'image <span style={{ fontWeight: 400, color: '#94a3b8' }}>(améliore la lisibilité du texte)</span>
            </label>
            <div style={s.twoCol}>
              <div>
                <label style={{ ...s.label, fontWeight: 400, fontSize: '0.78rem' }}>Couleur du voile</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="color" value={overlayColor} onChange={e => setOverlayColor(e.target.value)}
                    style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                  <input style={{ ...s.input, flex: 1 }} value={overlayColor} onChange={e => setOverlayColor(e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
              <div>
                <label style={{ ...s.label, fontWeight: 400, fontSize: '0.78rem' }}>Intensité du voile : {Math.round(overlayOpacity * 100)}%</label>
                <input type="range" min="0" max="1" step="0.05" value={overlayOpacity}
                  onChange={e => setOverlayOpacity(parseFloat(e.target.value))}
                  style={{ width: '100%', marginTop: 10 }} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <label style={s.label}>Couleurs du texte</label>
            <div style={s.twoCol}>
              <div>
                <label style={{ ...s.label, fontWeight: 400, fontSize: '0.78rem' }}>Titre principal</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="color" value={titleColor || '#1e293b'} onChange={e => setTitleColor(e.target.value)}
                    style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                  <input style={{ ...s.input, flex: 1 }} value={titleColor} onChange={e => setTitleColor(e.target.value)} placeholder="Par défaut" />
                </div>
              </div>
              <div>
                <label style={{ ...s.label, fontWeight: 400, fontSize: '0.78rem' }}>Sous-titre</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="color" value={subtitleColor || '#64748b'} onChange={e => setSubtitleColor(e.target.value)}
                    style={{ width: 44, height: 38, border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', padding: 2, background: 'white' }} />
                  <input style={{ ...s.input, flex: 1 }} value={subtitleColor} onChange={e => setSubtitleColor(e.target.value)} placeholder="Par défaut" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ SECTION VERSET ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
          <div>
            <div style={s.sTitle}>Verset biblique</div>
            <div style={s.sDesc}>Citation affichée sous le titre — laisser vide pour masquer</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Verset</label>
            <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={verse}
              onChange={e => setVerse(e.target.value)}
              placeholder="Car je connais les projets que j'ai formés sur vous, dit l'Éternel..." />
          </div>
          <div>
            <label style={s.label}>Référence</label>
            <input style={{ ...s.input, maxWidth: 240 }} value={verseRef}
              onChange={e => setVerseRef(e.target.value)} placeholder="Jérémie 29:11" />
          </div>
          {(verse || verseRef) && (
            <div style={{ marginTop: 16, padding: '14px 18px', background: '#faf5ff', borderRadius: 10, border: '1px solid #e9d5ff' }}>
              <div style={{ fontSize: '0.78rem', color: '#7c3aed', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Aperçu</div>
              {verse && <p style={{ fontStyle: 'italic', color: '#374151', fontSize: '0.9rem', margin: '0 0 6px', lineHeight: 1.7 }}>« {verse} »</p>}
              {verseRef && <p style={{ color: '#7c3aed', fontWeight: 700, fontSize: '0.82rem', margin: 0 }}>— {verseRef}</p>}
            </div>
          )}
        </div>
      </div>

      {/* ═══ SECTION ACTIVITÉS ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <div>
            <div style={s.sTitle}>Section Activités</div>
            <div style={s.sDesc}>Affichage des prochains événements planifiés</div>
          </div>
        </div>
        <div style={s.sBody}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16, padding: '12px 14px', background: showActivities ? '#f0fdf4' : 'var(--page-bg)', borderRadius: 10, border: `1px solid ${showActivities ? '#bbf7d0' : 'var(--border)'}`, transition: 'all 0.15s' }}>
            <input type="checkbox" checked={showActivities} onChange={e => setShowActivities(e.target.checked)}
              style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#7c3aed' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>Afficher les activités planifiées / en cours</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Les fidèles et visiteurs pourront voir les prochains événements sans se connecter</div>
            </div>
          </label>
          {showActivities && (
            <div>
              <label style={s.label}>Titre de la section</label>
              <input style={{ ...s.input, maxWidth: 320 }} value={activitiesTitle}
                onChange={e => setActivitiesTitle(e.target.value)} placeholder="Activités à venir" />
            </div>
          )}
        </div>
      </div>

      {/* ═══ SECTION FONCTIONNALITÉS ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          <div>
            <div style={s.sTitle}>Section Fonctionnalités</div>
            <div style={s.sDesc}>{features.length} carte{features.length > 1 ? 's' : ''} — présentez les points forts de votre plateforme</div>
          </div>
        </div>
        <div style={s.sBody}>
          {features.map((f, i) => (
            <div key={i} style={{ background: 'var(--page-bg)', borderRadius: 12, border: '1px solid var(--border)', padding: '16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, fontSize: '0.65rem', fontWeight: 800, color: '#7c3aed' }}>
                  {i + 1}
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)', flex: 1 }}>Carte {i + 1}</span>
                {features.length > 1 && (
                  <button onClick={() => setFeatures(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.72rem', color: '#dc2626', fontWeight: 700 }}>
                    Supprimer
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, marginBottom: 10 }}>
                <div>
                  <label style={s.label}>Icône</label>
                  <select style={{ ...s.input, cursor: 'pointer' }} value={f.icon}
                    onChange={e => updateFeature(i, 'icon', e.target.value)}>
                    {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Titre</label>
                  <input style={s.input} value={f.title}
                    onChange={e => updateFeature(i, 'title', e.target.value)}
                    placeholder="Titre de la fonctionnalité" />
                </div>
              </div>
              <div>
                <label style={s.label}>Description</label>
                <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={f.desc}
                  onChange={e => updateFeature(i, 'desc', e.target.value)}
                  placeholder="Description courte et percutante..." />
              </div>
            </div>
          ))}

          {features.length < 6 && (
            <button
              onClick={() => setFeatures(prev => [...prev, { icon: 'check', title: 'Nouvelle fonctionnalité', desc: 'Décrivez cette fonctionnalité en une phrase.' }])}
              style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Ajouter une carte
            </button>
          )}
        </div>
      </div>

      {/* ═══ SECTION ACTUALITÉS ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          <div>
            <div style={s.sTitle}>Affiches & Événements</div>
            <div style={s.sDesc}>{news.length} affiche{news.length > 1 ? 's' : ''} — image de l'événement + lien YouTube, Facebook, TikTok...</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe', fontSize: '0.78rem', color: '#4338ca', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Ajoutez une URL d'image (affiche) et un lien vers YouTube, Facebook, TikTok etc. Les visiteurs cliqueront pour aller sur la vidéo ou la page.
          </div>

          {news.map((item, i) => (
            <AfficheCard key={i} item={item} index={i}
              onChange={(updates) => setNews(prev => prev.map((x,j) => j===i ? {...x,...updates} : x))}
              onDelete={() => setNews(prev => prev.filter((_,j) => j !== i))}
              s={s}
            />
          ))}

          <button
            onClick={() => setNews(prev => [...prev, { title: '', desc: '', image_url: '', link_url: '', link_type: 'link', badge: '' }])}
            style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter une actualité
          </button>
        </div>
      </div>

      {/* ═══ SECTION TÉMOIGNAGES ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <div>
            <div style={s.sTitle}>Témoignages</div>
            <div style={s.sDesc}>{testimonials.length} témoignage{testimonials.length > 1 ? 's' : ''} — avis de pasteurs, responsables ou fidèles affichés sur la page d'accueil</div>
          </div>
        </div>
        <div style={s.sBody}>
          {testimonials.map((t, i) => (
            <div key={i} style={{ marginBottom: 14, padding: 14, background: 'var(--page-bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b' }}>Témoignage {i + 1}</span>
                <button onClick={() => setTestimonials(prev => prev.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                  Supprimer
                </button>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={s.label}>Citation</label>
                <textarea value={t.quote} onChange={e => setTestimonials(prev => prev.map((x, j) => j === i ? { ...x, quote: e.target.value } : x))}
                  placeholder="Ce que dit la personne..." rows={2}
                  style={{ ...s.input, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={s.twoCol}>
                <div>
                  <label style={s.label}>Nom</label>
                  <input value={t.name} onChange={e => setTestimonials(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder="Ex : Pasteur Emmanuel K." style={s.input} />
                </div>
                <div>
                  <label style={s.label}>Rôle / Église</label>
                  <input value={t.role} onChange={e => setTestimonials(prev => prev.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                    placeholder="Ex : RCC · Abidjan" style={s.input} />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={s.label}>Note (étoiles)</label>
                <select value={t.stars || 5} onChange={e => setTestimonials(prev => prev.map((x, j) => j === i ? { ...x, stars: Number(e.target.value) } : x))} style={s.input}>
                  {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} étoile{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>
          ))}

          <button
            onClick={() => setTestimonials(prev => [...prev, { quote: '', name: '', role: '', stars: 5 }])}
            style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter un témoignage
          </button>
        </div>
      </div>

      {/* ═══ SECTION QUI SOMMES-NOUS / VISION ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
          <div>
            <div style={s.sTitle}>Qui sommes-nous / Notre vision</div>
            <div style={s.sDesc}>Présentez votre église et sa vision sur la page d'accueil (section optionnelle)</div>
          </div>
        </div>
        <div style={s.sBody}>
          <div style={s.subHead}><span style={s.dot}/>Qui sommes-nous</div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Titre</label>
            <input style={s.input} value={aboutTitle} onChange={e => setAboutTitle(e.target.value)} placeholder="Qui sommes-nous" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Texte de présentation</label>
            <textarea style={{ ...s.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }} value={aboutText} onChange={e => setAboutText(e.target.value)} placeholder="Présentez votre église, son histoire, ses valeurs..." />
          </div>
          <div style={{ marginBottom: 22 }}>
            <label style={s.label}>Photo (optionnelle)</label>
            {aboutImageUrl && !removeAboutImage && (
              <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
                <img src={aboutImageUrl} alt="Aperçu" style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <button onClick={() => { setRemoveAboutImage(true); setAboutImageFile(null); }}
                  style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1 }}>✕</button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) { setAboutImageFile(f); setRemoveAboutImage(false); } }} style={{ display: 'block', fontSize: '0.85rem' }} />
          </div>

          <div style={s.subHead}><span style={s.dot}/>Notre vision</div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Titre</label>
            <input style={s.input} value={visionTitle} onChange={e => setVisionTitle(e.target.value)} placeholder="Notre vision" />
          </div>
          <div>
            <label style={s.label}>Texte</label>
            <textarea style={{ ...s.input, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }} value={visionText} onChange={e => setVisionText(e.target.value)} placeholder="Décrivez la vision et la mission de votre église..." />
          </div>
        </div>
      </div>

      {/* ═══ SECTION DÉPARTEMENTS ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <div>
            <div style={s.sTitle}>Nos départements</div>
            <div style={s.sDesc}>{departments.length} département{departments.length > 1 ? 's' : ''} — les différents services de votre église</div>
          </div>
        </div>
        <div style={s.sBody}>
          {departments.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start', padding: 12, background: 'var(--page-bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <input style={{ ...s.input, marginBottom: 8 }} value={d.name || ''} placeholder="Nom du département (ex: Jeunesse)"
                  onChange={e => setDepartments(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }} value={d.desc || ''} placeholder="Description courte"
                  onChange={e => setDepartments(prev => prev.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} />
              </div>
              <button onClick={() => setDepartments(prev => prev.filter((_, j) => j !== i))}
                style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
            </div>
          ))}
          <button
            onClick={() => setDepartments(prev => [...prev, { name: '', desc: '' }])}
            style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter un département
          </button>
        </div>
      </div>

      {/* ═══ SECTION PROGRAMMES ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <div>
            <div style={s.sTitle}>Nos programmes</div>
            <div style={s.sDesc}>{programs.length} programme{programs.length > 1 ? 's' : ''} — les rendez-vous réguliers de la communauté</div>
          </div>
        </div>
        <div style={s.sBody}>
          {programs.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start', padding: 12, background: 'var(--page-bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={s.twoCol}>
                  <input style={{ ...s.input, marginBottom: 8 }} value={p.name || ''} placeholder="Nom (ex: École du dimanche)"
                    onChange={e => setPrograms(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <input style={{ ...s.input, marginBottom: 8 }} value={p.schedule || ''} placeholder="Horaire (ex: Dimanche 08h30)"
                    onChange={e => setPrograms(prev => prev.map((x, j) => j === i ? { ...x, schedule: e.target.value } : x))} />
                </div>
                <textarea style={{ ...s.input, minHeight: 50, resize: 'vertical', fontFamily: 'inherit' }} value={p.desc || ''} placeholder="Description courte"
                  onChange={e => setPrograms(prev => prev.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} />
              </div>
              <button onClick={() => setPrograms(prev => prev.filter((_, j) => j !== i))}
                style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
            </div>
          ))}
          <button
            onClick={() => setPrograms(prev => [...prev, { name: '', desc: '', schedule: '' }])}
            style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter un programme
          </button>
        </div>
      </div>

      {/* ═══ SECTION DIFFUSION EN DIRECT ═══ */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
          <div>
            <div style={s.sTitle}>Diffusion en direct (Live)</div>
            <div style={s.sDesc}>Intégrez votre flux YouTube/Facebook Live sur la page d'accueil</div>
          </div>
        </div>
        <div style={s.sBody}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={liveEnabled} onChange={e => setLiveEnabled(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151' }}>Activer la section diffusion en direct</span>
          </label>
          {liveEnabled && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={s.label}>Titre affiché</label>
                <input style={s.input} value={liveTitle} onChange={e => setLiveTitle(e.target.value)} placeholder="Diffusion en direct" />
              </div>
              <div>
                <label style={s.label}>URL d'intégration (embed)</label>
                <input style={s.input} value={liveUrl} onChange={e => setLiveUrl(e.target.value)} placeholder="https://www.youtube.com/embed/xxxx ou https://www.facebook.com/plugins/video.php?href=..." />
                <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 5 }}>Utilisez le lien "intégrer" (embed) de YouTube ou Facebook, pas le lien de la vidéo normale.</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Bouton bas de page ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8 }}>
        <a href="/" target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#7c3aed', fontWeight: 600, textDecoration: 'none', padding: '9px 14px', border: '1px solid #e9d5ff', borderRadius: 8, background: '#faf5ff' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          Voir la page
        </a>
        <button onClick={handleSave} disabled={saving}
          style={{ backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '9px 28px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.92rem', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Sauvegarde…' : 'Enregistrer la page'}
        </button>
      </div>
    </div>
  );
}
